/* eslint-disable no-nested-ternary */
import 'add-to-calendar-button/unstyle';
import '@brands/Dashboard/MultiStage/multiStage.scss';

import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import CancelButton from '@brands/Dashboard/Appointments/Patient/AppointmentCard/CancelButton';
import EditCreditCardButton from '@brands/Dashboard/Appointments/Patient/AppointmentCard/EditCreditCardButton';
import JoinButton from '@brands/Dashboard/Appointments/Patient/AppointmentCard/JoinButton';
import RescheduleButton from '@brands/Dashboard/Appointments/Patient/AppointmentCard/RescheduleButton';
import CancelAppointmentModal from '@brands/Dashboard/Appointments/Patient/CancelAppointmentModal/CancelAppointmentModal';
import { statusLabel, statusVariant } from '@brands/Dashboard/MultiStage/multiStageUi';
import styles from '@brands/Dashboard/Dashboard/Patient/components/styles.module.scss';
import SmileScanStar from '@brands/Dashboard/Dashboard/Patient/utils/smile_scan_star.svg';
import { useWindowSize } from '@brands/hooks';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import useSubscribe, { Subscriber } from '@brands/hooks/useSubscribe';
import { startLinkedCaseCompletion } from '@brands/Dashboard/MultiStage/utils/completeLinkedCase';
import { CaseType, ICase, Pricing, Status } from '@brands/services/cases/types/ICase';
import { selectDependents } from '@brands/store/selectors/dependents';
import { selectOrganizationPrices } from '@brands/store/selectors/organizationPrices';
import { getFormatDateFullDate } from '@brands/Utils/formatDate';
import {
  addAdditionalInformationAvailability,
  cancelCaseAvailability,
  getFriendlyName,
  getFriendlyNameOfCaseType,
  getFriendlyNameOfConsultationType,
  rescheduleCaseAvailability,
  updatePaymentAvailability,
} from '@brands/Utils/getFriendlyName';
import { getButtonHoverColor, getPrimaryColor, getSecondary } from '@brands/Utils/getPrimaryColor';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonIcon from '@mui/icons-material/Person';
import Button from '@mui/material/Button';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';

import AddAdditionalInformationButton from '../../../Appointments/Patient/AppointmentCard/AddAdditionalInformationButton';
import ProfileComponent from '../../CareAdvisor/Component/ProfileComponent';
import AdditionalInformationModal from '../../Components/AdditionalInformationModal/AdditionalInformationModal';

type AppointmentsProps = {
  displayingCases: ICase[];
  dataLoading: boolean;
  reloadAll: () => Promise<void>;
  setEditCreditCardModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCaseFromUpcomingServices: ICase | undefined;
  setSelectedCaseFromUpcomingServices: React.Dispatch<React.SetStateAction<ICase | undefined>>;
  isDashboard?: boolean;
  lastMyCasesItemRef?: React.RefObject<HTMLTableRowElement>;
  isLoading?: boolean;
  /**
   * Renders inside the appointment card, flush against its bottom edge — used
   * for the care journey strip. Returning null leaves the card untouched.
   */
  renderCaseFooter?: (caseItem: ICase) => React.ReactNode;
  /**
   * Case ids whose intake is still outstanding — shows an "Action needed"
   * pill plus a "Complete Intake" button on that case's row. Opt-in (the My
   * Care Journey page is the only caller today), so omitting it leaves every
   * other UpcomingServices usage unchanged.
   */
  needsIntakeCaseIds?: Set<number>;
  /**
   * Shows a status pill (Upcoming / In progress / …) for a Care Coordination
   * case. Care coordination is async, so it never gets a video_conference_date
   * like the scheduled call types do; its progress is read instead from
   * caseItem.status via the same statusLabel/statusVariant map the journey
   * and profile views use. Opt-in for the same reason as needsIntakeCaseIds.
   */
  showCareCoordinationScheduleStatus?: boolean;
};

const appointmentDateCaseTypes: CaseType[] = [
  CaseType.video_call_scheduled,
  CaseType.video_call_instant,
  CaseType.phone_call_scheduled,
  CaseType.oral_health_coaching,
];

const UpcomingServices = ({
  displayingCases,
  dataLoading,
  reloadAll,
  setEditCreditCardModal,
  selectedCaseFromUpcomingServices,
  setSelectedCaseFromUpcomingServices,
  isDashboard,
  lastMyCasesItemRef,
  isLoading,
  renderCaseFooter,
  needsIntakeCaseIds,
  showCareCoordinationScheduleStatus,
}: AppointmentsProps): JSX.Element => {
  const roleNavigate = useRoleNavigate();
  const dispatch = useAppDispatch();
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const { dependents } = useAppSelector(selectDependents);
  const currentOrganizationPrices = useAppSelector(selectOrganizationPrices);
  const [editAppointmentDropDown, setOpenAppointmentDropDown] = useState(0);
  const ref = useDetectClickOutside({ onTriggered: () => setOpenAppointmentDropDown(0) });
  const [cancelCaseModal, setCancelCaseModal] = useState<boolean>(false);
  const screenSize = useWindowSize();
  const isMobile = screenSize.width < 768;
  const [additionalInformationModal, setAdditionalInformationModal] = useState<boolean>(false);

  const checkIsDependentActive = (appointment: ICase): boolean => {
    const isDependent = appointment.patient?.guardian_id;
    if (!isDependent) return true;
    const dependentDetails = dependents
      .filter((dependent) => dependent?.status !== 'Deactivated')
      .find((dependent) => Number(dependent?.value) === Number(appointment.patient?.id));
    const isDeactivated = isDependent && dependentDetails?.status === 'Active';
    return isDeactivated || true;
  };

  const shouldShowControls = (caseItem: ICase): boolean => {
    return (
      !isMobile ||
      !!needsIntakeCaseIds?.has(caseItem.id) ||
      (caseItem &&
        ([
          'video_call_scheduled',
          'video_call_instant',
          'oral_health_coaching',
          CaseType.smile_scan,
          CaseType.one_report,
          CaseType.phone_call_scheduled,
          CaseType.second_opinion,
        ].includes(caseItem.type) ||
          caseItem.patient_care_advisor_conversation_sid ||
          caseItem.patient_provider_conversation_sid ||
          (caseItem.type !== CaseType.care_coordination &&
            ((checkIsDependentActive(caseItem) &&
              Array.isArray(currentOrganizationPrices.currentOrganizationPrices) &&
              currentOrganizationPrices.currentOrganizationPrices
                ?.find((pricing: Pricing) => pricing.case_types.includes(caseItem.type))
                ?.payment_options.find((paymentOption: string) => paymentOption === 'Credit Card') &&
              updatePaymentAvailability(caseItem)) ||
              cancelCaseAvailability(caseItem) ||
              rescheduleCaseAvailability(caseItem) ||
              addAdditionalInformationAvailability(caseItem)))))
    );
  };

  const setOpenEditAppointment = (appointmentId: number): void => {
    setOpenAppointmentDropDown((prev) => (prev === appointmentId ? 0 : appointmentId));
  };

  const subscriber: Subscriber = useMemo(
    () => ({
      onMessage: (message: Record<string, any>) => {
        if (
          [
            'case_updated',
            'case_new_message_created',
            'case_cancelled',
            'case_new_conversation_created',
            'case_completed',
          ].includes(message.command)
        ) {
          reloadAll();
        }
      },
      commands: [
        'case_updated',
        'case_new_message_created',
        'case_cancelled',
        'case_new_conversation_created',
        'case_completed',
      ],
    }),

    [reloadAll],
  );
  const { subscribe, unsubscribe } = useSubscribe();

  useEffect(() => {
    const subscriptionId = subscribe(subscriber);

    return () => unsubscribe(subscriptionId);
  }, [subscribe, unsubscribe, subscriber]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .atcb-button {
        background: none !important;
        border: none !important;
        box-shadow: none !important;
        color: inherit !important;
      }
      .atcb-button svg {
        display: none !important;
      }
      .atcb-button.custom {
        text-transform: lowercase !important;
      }
    `;
    document.head.appendChild(style);
  }, []);

  return (
    <div className={`${styles.upcomingServicesContainer} fs-unmask`}>
      {isDashboard && (
        <div className={styles.upcomingConsultationsTitle}>
          <span className="fs-unmask">Upcoming appointments</span>
        </div>
      )}
      {dataLoading ? (
        <Loading dotted />
      ) : (
        <div className={styles.upcomingConsultationsDiv}>
          {(isDashboard ? displayingCases.slice(0, 3) : displayingCases).map((caseItem, index, array) => {
            const isLastItem = index === array.length - 1;
            const hasAppointmentDate = appointmentDateCaseTypes.includes(caseItem.type);
            const isClosedCase = caseItem.status === Status.Canceled || caseItem.status === Status.Completed;
            // Care coordination / OHC cases are run by a care advisor, not a
            // provider, so the "primary person" whose assignment drives the
            // avatar/title is the CA for those types and the provider otherwise.
            const isCareAdvisorPrimary =
              caseItem.type === CaseType.care_coordination || caseItem.type === CaseType.oral_health_coaching;
            const primaryPerson = isCareAdvisorPrimary ? caseItem.care_advisor : caseItem.provider;
            // A case completed by the staff status override has no report to open.
            const canViewReport =
              caseItem.status === Status.Completed &&
              caseItem.type !== CaseType.care_coordination &&
              Boolean(caseItem.report_available);
            const caseFooter = renderCaseFooter?.(caseItem);
            return (
              <div
                key={caseItem.id}
                className={`${styles.upcomingConsultations}${caseFooter ? ` ${styles.journeyCardStack}` : ''}`}
                ref={lastMyCasesItemRef && isLastItem ? lastMyCasesItemRef : null}
              >
                <div className={`${styles.appointmentDivContainer}${caseFooter ? ` ${styles.hasJourneyStrip}` : ''}`}>
                  <div className={styles.appointmentLeftSide}>
                    <div className={styles.appointmentDetails}>
                      {caseItem.type !== CaseType.smile_scan ? (
                        <div className={styles.providerImage}>
                          {!primaryPerson ? (
                            <div className={styles.providerImagePlaceholder}>
                              <PersonIcon fontSize="inherit" />
                            </div>
                          ) : isCareAdvisorPrimary ? (
                            <ProfileComponent caseRow={caseItem} displayRole={false} role="CA" showName={false} />
                          ) : (
                            <ProfileComponent caseRow={caseItem} displayRole={false} role="Provider" showName={false} />
                          )}
                        </div>
                      ) : (
                        <div>
                          <img className={styles.smileScanStar} src={SmileScanStar} alt="SmileScan" />
                        </div>
                      )}
                      {caseItem.type !== CaseType.smile_scan ? (
                        <div className={styles.appointmentInfo}>
                          <span className={`${styles.appointmentState} fs-unmask`}>
                            <strong>
                              {primaryPerson
                                ? getFriendlyNameOfConsultationType(caseItem.type, isClosedCase)
                                : getFriendlyName(caseItem.type) || getFriendlyNameOfCaseType(caseItem.type)}
                            </strong>
                            {/* closed-case friendly names already end in "with"/"by" */}
                            {primaryPerson && !isClosedCase && `\u00A0with`}
                          </span>
                          {caseItem.provider && !isCareAdvisorPrimary && (
                            <span className={styles.providerName}>
                              <ProfileComponent
                                caseRow={caseItem}
                                displayRole={false}
                                role="Provider"
                                showImage={false}
                                maxChars={25}
                                headLine
                              />
                            </span>
                          )}
                          {isCareAdvisorPrimary && caseItem.care_advisor && (
                            <span className={styles.providerName}>
                              <ProfileComponent
                                caseRow={caseItem}
                                displayRole={false}
                                role="CA"
                                showImage={false}
                                maxChars={25}
                                headLine
                              />
                            </span>
                          )}
                          {hasAppointmentDate && (
                            <span className={`${styles.appointmentVideoDate} fs-unmask`}>
                              {caseItem.video_conference_date !== null ? (
                                <span>{getFormatDateFullDate(caseItem.video_conference_date || '', timeZone)}</span>
                              ) : (
                                <span>Not yet scheduled</span>
                              )}
                            </span>
                          )}

                          {!isClosedCase && hasAppointmentDate && caseItem.video_conference_date && (
                            <span className={`${styles.appointmentAddToCalendarButton} fs-unmask`}>
                              <AddToCalendarButton
                                name={`Dentistry.one ${getFriendlyNameOfConsultationType(caseItem.type, false)}`}
                                options={['Google', 'iCal', 'Outlook.com']}
                                description={`${getFriendlyNameOfConsultationType(caseItem.type, false)}`}
                                location="World Wide Web"
                                startDate={moment.utc(caseItem.video_conference_date).tz(timeZone).format('YYYY-MM-DD')}
                                endDate={moment.utc(caseItem.video_conference_date).tz(timeZone).format('YYYY-MM-DD')}
                                startTime={moment.utc(caseItem.video_conference_date).tz(timeZone).format('HH:mm')}
                                endTime={moment
                                  .utc(caseItem.video_conference_date)
                                  .tz(timeZone)
                                  .add(1, 'hour')
                                  .format('HH:mm')}
                                useUserTZ
                                timeZone={timeZone}
                                hideIconButton
                                label="Add to calendar"
                                trigger="click"
                                customCss="/styles.css"
                                buttonStyle="custom"
                              />
                            </span>
                          )}
                          {caseItem.status === Status.Canceled && (
                            <span className={styles.canceledCase}>Appointment canceled</span>
                          )}
                          {caseItem.status === Status.Completed && (
                            <span className={styles.completedCase}>Appointment completed</span>
                          )}
                          {showCareCoordinationScheduleStatus &&
                            caseItem.type === CaseType.care_coordination &&
                            !isClosedCase &&
                            !needsIntakeCaseIds?.has(caseItem.id) && (
                              <span
                                className={`ms-pill ${statusVariant(caseItem.status)}`}
                                style={{ marginTop: 6, width: 'fit-content' }}
                              >
                                {statusLabel(caseItem.status)}
                              </span>
                            )}
                          {needsIntakeCaseIds?.has(caseItem.id) && (
                            <span className="ms-pill actionneeded" style={{ marginTop: 6, width: 'fit-content' }}>
                              Action needed
                            </span>
                          )}
                          {caseItem.type !== CaseType.care_coordination &&
                            caseItem.type !== CaseType.oral_health_coaching && (
                              <span className={`${styles.caNAme} ${styles.appointmentDetailsInfo}`}>
                                <span className="fs-unmask">Care Advisor</span>
                                <span>
                                  <ProfileComponent
                                    caseRow={caseItem}
                                    displayRole={false}
                                    role="CA"
                                    showImage={false}
                                    maxChars={25}
                                  />
                                </span>
                              </span>
                            )}
                        </div>
                      ) : (
                        <div className={styles.appointmentInfo}>
                          <span className={`${styles.appointmentStateSmileScan} fs-unmask`}>SmileScan™</span>
                          <span className={styles.smileScanDate}>
                            {getFormatDateFullDate(caseItem.completed_at || '', timeZone)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {shouldShowControls(caseItem) && (
                    <div className={styles.appointmentRightSide}>
                      <div className={styles.appointmentControls}>
                        <div className={styles.controls}>
                          {needsIntakeCaseIds?.has(caseItem.id) && (
                            <Button
                              className="fs-unmask"
                              type="button"
                              variant="contained"
                              sx={{
                                backgroundColor: getPrimaryColor(),
                                textTransform: 'none',
                                color: '#fff',
                                borderRadius: 999,
                                '&:hover': {
                                  backgroundColor: getButtonHoverColor(),
                                },
                              }}
                              onClick={() => startLinkedCaseCompletion(caseItem, dispatch, roleNavigate)}
                            >
                              Complete Intake
                            </Button>
                          )}
                          {caseItem &&
                            !isClosedCase &&
                            !needsIntakeCaseIds?.has(caseItem.id) &&
                            (caseItem.type === 'video_call_scheduled' ||
                              caseItem.type === 'video_call_instant' ||
                              caseItem.type === 'oral_health_coaching') && <JoinButton appointment={caseItem} />}
                          {caseItem &&
                            caseItem.provider === null &&
                            !isClosedCase &&
                            (caseItem.type === 'video_call_scheduled' ||
                              caseItem.type === 'video_call_instant' ||
                              caseItem.type === 'oral_health_coaching') &&
                            rescheduleCaseAvailability(caseItem) && (
                              <Button
                                className={`${styles.appBtn} fs-unmask`}
                                type="button"
                                color="primary"
                                onClick={() => {
                                  if (!rescheduleCaseAvailability(caseItem)) return;

                                  roleNavigate(
                                    caseItem.type !== CaseType.oral_health_coaching
                                      ? '/select-provider'
                                      : '/select-care-advisor',
                                    {
                                      state: {
                                        RescheduleCase: true,
                                        caseId: caseItem.id,
                                        providerId: caseItem?.provider?.id || 0,
                                        careAdvisorId: caseItem?.care_advisor_id || 0,
                                        caseType: caseItem.type,
                                        patient_current_state: caseItem.patient_current_state,
                                        RescheduleCaseSelectedCarrierId:
                                          caseItem?.insurance?.insurance_membership?.insurance_carrier?.id || null,
                                      },
                                    },
                                  );
                                }}
                                variant="outlined"
                                sx={{ textTransform: 'none' }}
                              >
                                Reschedule
                              </Button>
                            )}

                          {canViewReport && (
                            <Button
                              className={`fs-unmask ${styles.actionBtnCompleted}`}
                              type="button"
                              variant="contained"
                              sx={{
                                backgroundColor: getSecondary(),
                                textTransform: 'none',
                                color: '#fff',
                                '&:hover': {
                                  backgroundColor: getButtonHoverColor(),
                                },
                              }}
                              onClick={() => {
                                caseItem.type === CaseType.smile_scan
                                  ? roleNavigate(`/smile-scan-report/${caseItem?.id}`)
                                  : caseItem.type !== CaseType.oral_health_coaching
                                  ? roleNavigate(`/oral-health-report/${caseItem?.id}`)
                                  : roleNavigate(`/oral-health-coaching-report/${caseItem?.id}`);
                              }}
                            >
                              <DescriptionOutlinedIcon className={styles.cReport} />
                              View Report
                            </Button>
                          )}
                          {(caseItem.patient_care_advisor_conversation_sid ||
                            caseItem.patient_provider_conversation_sid) && (
                            <Button
                              className={`${styles.appBtn} fs-unmask`}
                              type="button"
                              color="primary"
                              onClick={() => roleNavigate(`/case-chat/${caseItem?.id}`)}
                              variant="outlined"
                              sx={{ textTransform: 'none' }}
                            >
                              Messages
                            </Button>
                          )}

                          {caseItem.type !== CaseType.care_coordination &&
                            ((!needsIntakeCaseIds?.has(caseItem.id) &&
                              checkIsDependentActive(caseItem) &&
                              Array.isArray(currentOrganizationPrices.currentOrganizationPrices) &&
                              currentOrganizationPrices.currentOrganizationPrices?.find((pricing: Pricing) =>
                                pricing.case_types.includes(caseItem.type),
                              )?.payment_options &&
                              Array.isArray(
                                currentOrganizationPrices.currentOrganizationPrices?.find((pricing: Pricing) =>
                                  pricing.case_types.includes(caseItem.type),
                                )?.payment_options,
                              ) &&
                              currentOrganizationPrices.currentOrganizationPrices
                                ?.find((pricing: Pricing) => pricing.case_types.includes(caseItem.type))
                                ?.payment_options.find((paymentOption: string) => paymentOption === 'Credit Card') &&
                              updatePaymentAvailability(caseItem)) ||
                              cancelCaseAvailability(caseItem) ||
                              rescheduleCaseAvailability(caseItem) ||
                              addAdditionalInformationAvailability(caseItem)) && (
                              <div style={{ position: 'relative' }}>
                                <Button
                                  className={`${styles.appBtn} fs-unmask`}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenEditAppointment(caseItem.id);
                                  }}
                                  variant="outlined"
                                  sx={{ textTransform: 'none' }}
                                >
                                  Edit appointment
                                </Button>
                                {editAppointmentDropDown === caseItem.id && (
                                  <div className={`${styles.appointmentDropDown}`} ref={ref}>
                                    {addAdditionalInformationAvailability(caseItem) && (
                                      <span className={styles.optionSpan}>
                                        <AddAdditionalInformationButton
                                          appointment={caseItem}
                                          setAdditionalInformationModal={setAdditionalInformationModal}
                                          setCase={setSelectedCaseFromUpcomingServices}
                                        />
                                      </span>
                                    )}
                                    {!needsIntakeCaseIds?.has(caseItem.id) &&
                                      Array.isArray(currentOrganizationPrices.currentOrganizationPrices) &&
                                      currentOrganizationPrices.currentOrganizationPrices
                                        .find(
                                          (pricing: Pricing) =>
                                            Array.isArray(pricing.case_types) &&
                                            pricing.case_types.includes(caseItem.type),
                                        )
                                        ?.payment_options?.includes('Credit Card') &&
                                      updatePaymentAvailability(caseItem) && (
                                        <span className={styles.optionSpan}>
                                          <EditCreditCardButton
                                            appointment={caseItem}
                                            setEditCreditCardModal={setEditCreditCardModal}
                                            setCase={setSelectedCaseFromUpcomingServices}
                                            updatePaymentAvailabilityCheck={updatePaymentAvailability(caseItem)}
                                          />
                                        </span>
                                      )}
                                    {rescheduleCaseAvailability(caseItem) && (
                                      <span className={styles.optionSpan}>
                                        <RescheduleButton
                                          appointment={caseItem}
                                          rescheduleAvailability={rescheduleCaseAvailability(caseItem)}
                                        />
                                      </span>
                                    )}
                                    {cancelCaseAvailability(caseItem) && (
                                      <span className={styles.optionSpan}>
                                        <CancelButton
                                          appointment={caseItem}
                                          setCancelCaseModal={setCancelCaseModal}
                                          setCase={setSelectedCaseFromUpcomingServices}
                                          cancelAvailability={cancelCaseAvailability(caseItem)}
                                        />
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {caseFooter}
              </div>
            );
          })}
          {isLoading && <Loading dotted />}
        </div>
      )}
      {isDashboard && displayingCases.length > 3 && (
        <div className={`${styles.viewAllContainer} fs-unmask`}>
          <Button
            variant="contained"
            className={styles.viewAllButton}
            onClick={() => roleNavigate('/my-care#upcoming_appointments')}
            data-testid="dashboard-view-all-upcoming"
          >
            View all upcoming appointments
          </Button>
        </div>
      )}
      {isDashboard && (
        <div className={styles.controlsEdit}>
          <Button
            variant="text"
            className={`${styles.editBtn} fs-unmask`}
            type="button"
            onClick={() => roleNavigate(`/my-care#care_history`)}
          >
            View past care
          </Button>
        </div>
      )}
      {cancelCaseModal && (
        <CancelAppointmentModal
          setOpenModal={setCancelCaseModal}
          thisCase={selectedCaseFromUpcomingServices!}
          reloadAll={reloadAll}
          refreshAll={reloadAll}
        />
      )}
      {additionalInformationModal && (
        <AdditionalInformationModal
          isOpen={additionalInformationModal}
          toggleModal={setAdditionalInformationModal}
          caseId={selectedCaseFromUpcomingServices?.id.toString() || ''}
        />
      )}
    </div>
  );
};
export default UpcomingServices;
